import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { Lang } from '../types';
interface TestimonialsProps {
  lang: Lang;
}
const content = {
  en: {
    title: 'What Clients Say',
    subtitle: 'Real reviews from real people',
    testimonials: [{
      name: 'Daniela R.',
      location: 'Miami, FL',
      text: 'Julian understood exactly what I wanted. The fine line work is incredibly clean — my tattoo looks even better than I imagined. Professional from start to finish.',
      tattoo: 'Floral arm piece',
      stars: 5
    }, {
      name: 'Carlos M.',
      location: 'Doral, FL',
      text: 'I was nervous about my first tattoo but Julian made the whole experience super comfortable. The studio is clean, the vibe is chill, and the result? Perfect.',
      tattoo: 'Minimalist geometric',
      stars: 5
    }, {
      name: 'Sarah K.',
      location: 'Brickell, FL',
      text: "Best fine line artist I've found in Miami. He took my rough idea and turned it into something beautiful. Already planning my next piece with him.",
      tattoo: 'Custom lettering',
      stars: 5
    }, {
      name: 'Andrés P.',
      location: 'Coral Gables, FL',
      text: 'Barboxa is a true artist. He advised me on placement and size, and the final result was way beyond my expectations. Highly recommend.',
      tattoo: 'Fine line portrait',
      stars: 5
    }, {
      name: 'Michelle T.',
      location: 'Wynwood, FL',
      text: 'The attention to detail is insane. Every line is precise and intentional. Julian really cares about delivering quality work, not just quantity.',
      tattoo: 'Botanical sleeve',
      stars: 5
    }, {
      name: 'David L.',
      location: 'Miami Beach, FL',
      text: 'Drove 45 minutes to get tattooed by Julian and it was 100% worth it. The design process was collaborative and the execution was flawless.',
      tattoo: 'Micro realism',
      stars: 5
    }]
  },
  es: {
    title: 'Lo que dicen los clientes',
    subtitle: 'Reseñas reales de personas reales',
    testimonials: [{
      name: 'Daniela R.',
      location: 'Miami, FL',
      text: 'Julián entendió exactamente lo que quería. El trabajo de línea fina es increíblemente limpio — mi tatuaje se ve incluso mejor de lo que imaginé. Profesional de principio a fin.',
      tattoo: 'Pieza floral en brazo',
      stars: 5
    }, {
      name: 'Carlos M.',
      location: 'Doral, FL',
      text: 'Estaba nervioso por mi primer tatuaje pero Julián hizo toda la experiencia súper cómoda. El estudio está limpio, el ambiente es tranquilo, y el resultado? Perfecto.',
      tattoo: 'Geométrico minimalista',
      stars: 5
    }, {
      name: 'Sarah K.',
      location: 'Brickell, FL',
      text: 'El mejor artista de línea fina que he encontrado en Miami. Tomó mi idea básica y la convirtió en algo hermoso. Ya estoy planeando mi siguiente pieza con él.',
      tattoo: 'Lettering personalizado',
      stars: 5
    }, {
      name: 'Andrés P.',
      location: 'Coral Gables, FL',
      text: 'Barboxa es un verdadero artista. Me asesoró sobre ubicación y tamaño, y el resultado final superó mis expectativas. Totalmente recomendado.',
      tattoo: 'Retrato fine line',
      stars: 5
    }, {
      name: 'Michelle T.',
      location: 'Wynwood, FL',
      text: 'La atención al detalle es increíble. Cada línea es precisa e intencional. Julián realmente se preocupa por entregar trabajo de calidad, no solo cantidad.',
      tattoo: 'Manga botánica',
      stars: 5
    }, {
      name: 'David L.',
      location: 'Miami Beach, FL',
      text: 'Manejé 45 minutos para tatuarme con Julián y valió 100% la pena. El proceso de diseño fue colaborativo y la ejecución fue impecable.',
      tattoo: 'Micro realismo',
      stars: 5
    }]
  }
};
export function TestimonialsSection({
  lang
}: TestimonialsProps) {
  const t = content[lang];
  return <section id="testimonials" className="py-12 md:py-16 bg-studio-bgAlt border-y border-studio-border" data-id="element-286">
      <div className="max-w-5xl mx-auto px-6" data-id="element-287">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-8 md:mb-10" data-id="element-288">
          <h2 className="text-3xl md:text-5xl font-bold mb-2" data-id="element-289">{t.title}</h2>
          <p className="text-studio-muted font-playfair italic text-lg" data-id="element-290">
            {t.subtitle}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-id="element-291">
          {t.testimonials.map((item, i) => <motion.div key={item.name} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.08
        }} className="bg-studio-bg border border-studio-border p-5 flex flex-col justify-between group hover:border-studio-accent/40 transition-colors duration-300" data-id="element-292">
              <div data-id="element-293">
                {/* Stars */}
                <div className="flex gap-0.5 mb-3" data-id="element-294">
                  {Array.from({
                length: item.stars
              }).map((_, s) => <Star key={s} size={14} className="text-studio-accent fill-studio-accent" data-id="element-295" />)}
                </div>

                {/* Quote */}
                <p className="text-studio-muted text-sm leading-relaxed mb-4" data-id="element-296">
                  "{item.text}"
                </p>
              </div>

              {/* Author */}
              <div className="pt-3 border-t border-studio-border/50" data-id="element-297">
                <p className="text-studio-text font-semibold text-sm" data-id="element-298">
                  {item.name}
                </p>
                <div className="flex items-center justify-between mt-0.5" data-id="element-299">
                  <span className="text-studio-muted text-xs" data-id="element-300">
                    {item.location}
                  </span>
                  <span className="text-studio-accent/60 text-xs italic" data-id="element-301">
                    {item.tattoo}
                  </span>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}